/* Tile faces — the printed artwork for all 27 tiles.
 *
 * CommonJS on purpose. React imports it through webpack's interop, and
 * scripts/build-harness.py requires it directly through node, so the static
 * harness and the running app draw from one definition rather than two
 * drifting copies of 27 layouts.
 *
 * The markup carries no tile frame. Bone face, rounded silhouette and the
 * crystalline bevel all come from .t-tile in CSS, so the artwork sits on
 * transparent and the bevel is not fighting an SVG border drawn over it.
 *
 * Numerals appear only on the character suit. Circles and bamboo are counted,
 * not read — that is the pattern-matching the game is played on, and printing
 * a digit in the corner would remove the thing that makes a board worth
 * looking at. Screen readers get it from the aria-label instead.
 */

const VIEWBOX = '0 0 60 84';

/* Pigment, not interface colour. These are inks printed on a tile face, so
 * they are deliberately separate from --rouge, which means one thing only
 * ("the action is on you") and must not pick up a second meaning from
 * appearing on every tile. Deep and slightly desaturated: enamelled rather
 * than printed, which is most of the difference between an heirloom set and
 * an airport one. */
const INK = {
  vermilion: '#A32A21',
  jade: '#2C5E46',
  jadeDeep: '#1E4231',
  sumi: '#2A2724',
  bone: '#F2EDE1',
};

/* ── Circles (筒子) ───────────────────────────────────────────────────────── */

const CIRCLE_LAYOUT = {
  1: { r: 22, pts: [[30, 42]] },
  2: { r: 14, pts: [[30, 23], [30, 61]] },
  3: { r: 11, pts: [[30, 17], [30, 42], [30, 67]] },
  4: { r: 10, pts: [[17, 23], [43, 23], [17, 61], [43, 61]] },
  5: { r: 9, pts: [[17, 17], [43, 17], [30, 42], [17, 67], [43, 67]] },
  6: { r: 8, pts: [[17, 17], [43, 17], [17, 42], [43, 42], [17, 67], [43, 67]] },
  7: { r: 7, pts: [[17, 13], [43, 13], [30, 27], [17, 42], [43, 42], [17, 68], [43, 68]] },
  8: { r: 7, pts: [[17, 12], [43, 12], [17, 30], [43, 30], [17, 55], [43, 55], [17, 73], [43, 73]] },
  9: { r: 7, pts: [[13, 13], [30, 13], [47, 13], [13, 42], [30, 42], [47, 42], [13, 71], [30, 71], [47, 71]] },
};

/* Concentric rings drawn as strokes rather than stacked filled discs. Strokes
 * hold their weight when the tile is 40px wide, and the thin bone gap between
 * the rings is what makes the pip look cut rather than printed. */
function pip(cx, cy, r) {
  return (
    `<circle cx="${cx}" cy="${cy}" r="${f(r * 0.86)}" fill="none" ` +
    `stroke="${INK.vermilion}" stroke-width="${f(r * 0.28)}"/>` +
    `<circle cx="${cx}" cy="${cy}" r="${f(r * 0.5)}" fill="none" ` +
    `stroke="${INK.jade}" stroke-width="${f(r * 0.24)}"/>` +
    `<circle cx="${cx}" cy="${cy}" r="${f(r * 0.16)}" fill="${INK.vermilion}"/>`
  );
}

/* ── Bamboo (索子) ───────────────────────────────────────────────────────── */

const BAMBOO_LAYOUT = {
  2: [{ x: 30, y: 8, h: 30, w: 13 }, { x: 30, y: 46, h: 30, w: 13 }],
  3: [{ x: 30, y: 7, h: 21, w: 12 }, { x: 30, y: 31, h: 21, w: 12 }, { x: 30, y: 55, h: 21, w: 12 }],
  4: [
    { x: 19, y: 8, h: 30, w: 11 }, { x: 41, y: 8, h: 30, w: 11 },
    { x: 19, y: 45, h: 30, w: 11 }, { x: 41, y: 45, h: 30, w: 11 },
  ],
  5: [
    { x: 19, y: 7, h: 21, w: 11 }, { x: 41, y: 7, h: 21, w: 11 },
    { x: 30, y: 31, h: 21, w: 11 },
    { x: 19, y: 55, h: 21, w: 11 }, { x: 41, y: 55, h: 21, w: 11 },
  ],
  6: [
    { x: 19, y: 7, h: 20, w: 10 }, { x: 41, y: 7, h: 20, w: 10 },
    { x: 19, y: 31, h: 20, w: 10 }, { x: 41, y: 31, h: 20, w: 10 },
    { x: 19, y: 55, h: 20, w: 10 }, { x: 41, y: 55, h: 20, w: 10 },
  ],
  7: [
    { x: 19, y: 7, h: 19, w: 10 }, { x: 41, y: 7, h: 19, w: 10 },
    { x: 11, y: 30, h: 19, w: 10 }, { x: 30, y: 30, h: 19, w: 10 }, { x: 49, y: 30, h: 19, w: 10 },
    { x: 19, y: 57, h: 19, w: 10 }, { x: 41, y: 57, h: 19, w: 10 },
  ],
  8: [
    { x: 13, y: 7, h: 18, w: 10 }, { x: 30, y: 7, h: 18, w: 10 }, { x: 47, y: 7, h: 18, w: 10 },
    { x: 20, y: 29, h: 18, w: 10 }, { x: 40, y: 29, h: 18, w: 10 },
    { x: 13, y: 55, h: 18, w: 10 }, { x: 30, y: 55, h: 18, w: 10 }, { x: 47, y: 55, h: 18, w: 10 },
  ],
  9: [
    { x: 13, y: 7, h: 18, w: 10 }, { x: 30, y: 7, h: 18, w: 10 }, { x: 47, y: 7, h: 18, w: 10 },
    { x: 13, y: 29, h: 18, w: 10 }, { x: 30, y: 29, h: 18, w: 10 }, { x: 47, y: 29, h: 18, w: 10 },
    { x: 13, y: 55, h: 18, w: 10 }, { x: 30, y: 55, h: 18, w: 10 }, { x: 47, y: 55, h: 18, w: 10 },
  ],
};

/* A length of cane. The previous version drew three pill-shaped segments at
 * rx = 34% of the width, which rounded every corner and read as mochi rather
 * than bamboo. Cane is a straight slender rod: near-square corners, one
 * continuous shaft, and two thin nodes banding across it. The nodes are what
 * make it bamboo — segmenting the shaft into three separate lozenges does
 * not. A single highlight down the left of the shaft gives it a round
 * section without softening the silhouette. */
function stalk({ x, y, h, w }) {
  const sw = w * 0.62;              // a rod, not a slab
  const x0 = f(x - sw / 2);
  const nodes = [y + h * 0.33, y + h * 0.67];
  let out =
    `<rect x="${x0}" y="${f(y)}" width="${f(sw)}" height="${f(h)}" ` +
    `rx="${f(sw * 0.16)}" fill="${INK.jade}" ` +
    `stroke="${INK.jadeDeep}" stroke-width="0.7"/>` +
    `<rect x="${f(x - sw / 2 + sw * 0.17)}" y="${f(y + 1.4)}" ` +
    `width="${f(sw * 0.2)}" height="${f(h - 2.8)}" rx="${f(sw * 0.1)}" ` +
    `fill="#3E7A5C" opacity="0.75"/>`;
  for (const ny of nodes) {
    out +=
      `<rect x="${f(x - sw / 2 - w * 0.14)}" y="${f(ny - 0.9)}" ` +
      `width="${f(sw + w * 0.28)}" height="1.8" rx="0.5" ` +
      `fill="${INK.jadeDeep}"/>`;
  }
  return out;
}

/* One of cords is the bird — the one figurative tile in the deck, and the
 * reason it is a bird at all is the game's name: 麻雀 means sparrow.
 *
 * Drawn as a seal impression: the bird cut out of a jade chop, the way a name
 * seal is stamped on a scroll. That puts it in the same calligraphic world as
 * the 萬 tiles, and it is the most robust option at small sizes because the
 * mass carrying the shape is the cartouche, not the creature — a silhouette
 * or a line drawing both close up and turn to mud at 32px.
 *
 * Jade rather than the cinnabar a real seal would be stamped in, so it stays
 * with the rest of its suit. Vermilion here would also have put a second
 * saturated red on the board competing with --rouge.
 */
function bird() {
  return (
    `<rect x="11" y="14.5" width="38" height="55" rx="5" fill="${INK.jade}"/>` +
    // The bird in negative: the bone of the tile face shows through the chop.
    `<path d="M36.5 27.5 C36.5 23.5 33.5 20.5 29.8 20.5 ` +
    `C26 20.5 23 23.5 23 27.5 C23 30.3 24.6 32.7 26.9 33.8 ` +
    `C25 38.5 23.6 44.6 23.6 50.5 C23.6 56 24.8 60.6 27 63.8 ` +
    `L22.5 65.8 L23.4 67.8 L31 64.8 C33.8 62.3 35.4 57.5 35.4 51.5 ` +
    `C35.4 44.5 33.4 37.5 30.9 32.8 C33.5 32 35.6 30.1 36.5 27.5 Z" ` +
    `fill="${INK.bone}"/>` +
    `<path d="M36.5 25.5 L42.5 27.6 L36.5 29.6 Z" fill="${INK.bone}"/>` +
    `<circle cx="31.4" cy="26.2" r="1.3" fill="${INK.jade}"/>` +
    // Two cord marks: this is still the one of its suit.
    `<rect x="38" y="46" width="2" height="16" rx="1" fill="${INK.bone}" ` +
    `opacity="0.9"/>` +
    `<rect x="42.5" y="49" width="2" height="10" rx="1" fill="${INK.bone}" ` +
    `opacity="0.55"/>`
  );
}

/* ── Characters (萬子) ───────────────────────────────────────────────────── */

const NUMERALS = ['一', '二', '三', '四', '五', '六', '七', '八', '九'];

/* Numeral in sumi above 萬 in vermilion. The English words the old faces
 * carried ("One", and "WAN" beneath it) are gone: a tile has a numeral and a
 * suit mark, and the transliteration is a souvenir-set tell. */
function character(value) {
  return (
    `<text x="30" y="34" text-anchor="middle" font-size="31" ` +
    `fill="${INK.sumi}" font-family="var(--font-tile)">` +
    `${NUMERALS[value - 1]}</text>` +
    `<text x="30" y="72" text-anchor="middle" font-size="29" ` +
    `fill="${INK.vermilion}" font-family="var(--font-tile)">萬</text>`
  );
}

/* ── Assembly ───────────────────────────────────────────────────────────── */

function f(n) {
  return Math.round(n * 10) / 10;
}

const SUIT_NAMES = { C: 'characters', B: 'bamboo', O: 'circles' };

function faceInner(suit, value) {
  if (suit === 'O') {
    const { r, pts } = CIRCLE_LAYOUT[value];
    return pts.map(([cx, cy]) => pip(cx, cy, r)).join('');
  }
  if (suit === 'B') {
    return value === 1 ? bird() : BAMBOO_LAYOUT[value].map(stalk).join('');
  }
  if (suit === 'C') return character(value);
  return '';
}

/** SVG markup for one tile face. Returns '' for anything unrecognised rather
 *  than throwing, so a malformed tile renders blank instead of taking the
 *  table down mid-hand. */
function tileFaceMarkup(suit, value) {
  const v = Number(value);
  if (!SUIT_NAMES[suit] || !Number.isInteger(v) || v < 1 || v > 9) return '';
  return (
    `<svg viewBox="${VIEWBOX}" width="100%" height="100%" ` +
    `role="img" aria-label="${v} of ${SUIT_NAMES[suit]}" ` +
    `xmlns="http://www.w3.org/2000/svg">${faceInner(suit, v)}</svg>`
  );
}

module.exports = { tileFaceMarkup, SUIT_NAMES, INK };
